<script lang="ts" setup>
import type { FeatureFlag } from '@forgd/shared/feature-flags'
import { ConsultationSourceEnum } from '@forgd/contract/types'
import { buildHomeNavDrawers, filterByFeatureAccess } from '#core/utils/nav-drawers'
import { socialLinks } from '#core/utils/external-links'

interface FooterLink {
  label: string
  to?: string
  action?: 'cookie-preferences'
  external?: boolean
  divider?: boolean
  featureFlag?: FeatureFlag
}

interface FooterColumn {
  title: string
  links: FooterLink[]
}

const allColumns = computed<FooterColumn[]>(() => [
  {
    title: 'Tools',
    links: buildHomeNavDrawers()['home-tools'].tiles.map(tile => ({ label: tile.label, to: tile.to })),
  },
  {
    title: 'Research & Insights',
    links: [
      { label: 'Tokenomics Analysis', to: '/research/tokenomics' },
      { label: 'Exchange Listing Trends', to: '/research/exchange-and-listing' },
      { label: 'Market Maker Leaderboard', to: '/market-maker-leaderboard' },
      { label: 'Universal Token Rating', to: '/universal-token-rating' },
      { label: 'Research reports', to: '/' },
      { label: 'Forgd Academy', to: '/academy', divider: true },
    ],
  },
  {
    title: 'About Forgd',
    links: [
      { label: 'What is Forgd?', to: '/about/what-is-forgd' },
      { label: 'Forgd Advisory', to: '/about/advisory' },
      { label: 'For Blockchain Projects', to: '/about/blockchain-projects' },
      { label: 'For Market Makers', to: '/about/market-makers' },
      { label: 'For Web3 Advisory Firms', to: '/about/blockchain-advisors' },
      { label: 'Pricing', to: '/about/pricing' },
      { label: 'Press & Blog', to: '/blog', external: true },
      { label: 'FAQs', to: '/about/faqs' },
      { label: 'Contact & Support', to: '/about/contact' },
      { label: 'Privacy Policy', to: '/privacy-policy' },
      { label: 'Terms & Conditions', to: '/terms-conditions' },
      { label: 'Cookie preferences', action: 'cookie-preferences' },
    ],
  },
])

const auth = useAuth()
const { enabled: cookieConsentEnabled, openPreferences: openCookiePreferences } = useCookieConsent()
const columns = computed(() =>
  allColumns.value.map(column => ({
    ...column,
    links: filterByFeatureAccess(column.links, { featureFlags: auth.featureFlags })
      .filter(link => link.action !== 'cookie-preferences' || cookieConsentEnabled),
  })),
)
</script>

<template>
  <footer data-el="CoreMarketingFooter" class="flex flex-col gap-12 pt-8 lg:flex-row lg:justify-between lg:gap-8">
    <div class="flex flex-col gap-10 lg:w-90 lg:shrink-0">
      <div class="flex flex-col gap-2">
        <h2 class="text-base font-semibold leading-6 text-primal">
          Smart data. Expert advisors.<br>Free specialized tools.
        </h2>
        <p class="max-w-90 text-sm leading-5.25 text-secondary">
          Forgd gives the advantage your blockchain project needs to launch and scale with confidence. All in a single place.
        </p>
      </div>

      <div class="flex items-center gap-8">
        <span class="text-sm text-secondary">Backed by</span>
        <div class="flex items-center gap-3">
          <img src="/images/backers/solana-foundation.svg" alt="Solana Foundation" class="h-4 w-auto">
          <img src="/images/backers/ava-labs.svg" alt="Ava Labs" class="h-4.5 w-auto">
        </div>
      </div>

      <CoreConsultationCard :source="ConsultationSourceEnum.PublicFooterCta" class="w-fit" />

      <div class="flex items-center gap-9">
        <a
          v-for="social in socialLinks"
          :key="social.label"
          :href="social.href"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="social.label"
          class="transition-opacity hover:opacity-70"
        >
          <img :src="social.icon" :alt="social.label" class="size-4">
        </a>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-3 lg:flex lg:min-w-0 lg:gap-15">
      <div v-for="column in columns" :key="column.title" class="flex flex-col gap-5 lg:w-50">
        <h3 class="text-sm font-semibold leading-5.25 text-primal">
          {{ column.title }}
        </h3>
        <ul class="flex flex-col gap-2">
          <li
            v-for="link in column.links"
            :key="link.label"
            :class="link.divider ? 'mt-1 border-t border-default pt-4' : ''"
          >
            <button
              v-if="link.action === 'cookie-preferences'"
              type="button"
              class="text-sm leading-5.25 text-secondary transition-colors hover:text-primal"
              @click="openCookiePreferences()"
            >
              {{ link.label }}
            </button>
            <NuxtLink
              v-else
              :to="link.to"
              :target="link.external ? '_blank' : undefined"
              :rel="link.external ? 'noopener noreferrer' : undefined"
              class="text-sm leading-5.25 text-secondary transition-colors hover:text-primal"
            >
              {{ link.label }}
            </NuxtLink>
          </li>
        </ul>
      </div>
    </div>
  </footer>
</template>
